"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useI18n } from "@/i18n/I18nProvider";
import { localizeError } from "@/i18n/errors";
import { AttachButton, buildChatForm, ChatImage, PendingStrip, usePendingImages } from "@/components/ChatAttachments";

interface Message {
  id: number;
  sender_type: "GUEST" | "STAFF" | "SYSTEM";
  sender_name: string | null;
  body: string;
  created_at: string;
  attachments: { id: string; mime_type: string; size_bytes: number; original_name: string | null }[];
}

type Creds = { kind: "inquiry"; threadId: string; token: string } | { kind: "booking"; reference: string; accessToken: string };

/** How a new conversation is started: from a booking site (visitor) or from the QR code in a room. */
export type ChatStart = { kind: "site"; hostname: string } | { kind: "room"; unitToken: string };

const parseTime = (s: string) => new Date(s.includes("T") ? s : `${s.replace(" ", "T")}Z`);

/**
 * The guest side of a conversation: history, live updates, and the form to start or continue it. It renders no frame, so
 * the booking-site widget (floating panel) and the room QR page (full page) can both host it. Keep it mounted while
 * hidden so new staff replies still arrive and `onUnread` can badge the launcher.
 */
export function GuestChatPanel({
  start,
  storageKey,
  booking,
  active,
  onUnread,
}: {
  start: ChatStart;
  storageKey: string;
  /** Present right after a booking is made; remembered so the guest can come back to the same conversation. */
  booking?: { reference: string; accessToken: string } | null;
  /** True while the panel is on screen: incoming replies are then marked read at once. */
  active: boolean;
  onUnread?: (count: number) => void;
}) {
  const { t, locale } = useI18n();
  const [creds, setCreds] = useState<Creds | null>(null);
  const [threadId, setThreadId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [unread, setUnread] = useState(0);
  const [name, setName] = useState("");
  const [contact, setContact] = useState("");
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [offline, setOffline] = useState(false);
  const [handler, setHandler] = useState<string | null>(null);
  const [staffReadUpto, setStaffReadUpto] = useState(0);
  const pending = usePendingImages();
  const [error, setError] = useState<string | null>(null);
  const activeRef = useRef(active);
  const bottomRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    activeRef.current = active;
  }, [active]);
  useEffect(() => {
    onUnread?.(unread);
  }, [unread, onUnread]);

  // Restore credentials: a booking made in this visit wins, then whatever this browser remembered.
  useEffect(() => {
    try {
      if (booking) {
        const c: Creds = { kind: "booking", reference: booking.reference, accessToken: booking.accessToken };
        localStorage.setItem(storageKey, JSON.stringify(c));
        setCreds(c);
        return;
      }
      const raw = localStorage.getItem(storageKey);
      if (raw) setCreds(JSON.parse(raw) as Creds);
    } catch {
      // Storage can be unavailable (private mode); the chat still works for this page view.
    }
  }, [booking, storageKey]);

  const headers = useCallback(
    (c: Creds): Record<string, string> =>
      c.kind === "inquiry" ? { "x-chat-token": c.token } : { "x-booking-reference": c.reference, "x-booking-token": c.accessToken },
    []
  );

  const markRead = useCallback(
    (c: Creds, threadId: string) => {
      setUnread(0);
      fetch(`/api/public/chat/threads/${threadId}/read`, { method: "POST", headers: headers(c) }).catch(() => {});
    },
    [headers]
  );

  // Load history (and, for a booking guest with none yet, nothing is created until they send a message).
  useEffect(() => {
    if (!creds) return;
    let cancelled = false;
    (async () => {
      try {
        let id: string | null = creds.kind === "inquiry" ? creds.threadId : null;
        if (creds.kind === "booking") {
          const res = await fetch("/api/public/chat/threads", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ booking_reference: creds.reference, access_token: creds.accessToken }),
          });
          const json = await res.json();
          if (!json.success) throw new Error(json.error);
          id = json.data.thread.id as string;
        }
        if (!id) return;
        const res = await fetch(`/api/public/chat/threads/${id}`, { headers: headers(creds) });
        const json = await res.json();
        if (!json.success) {
          // The remembered chat is gone or invalid: forget it and start fresh.
          if (creds.kind === "inquiry") {
            try { localStorage.removeItem(storageKey); } catch { /* ignore */ }
            if (!cancelled) {
              setCreds(null);
              setThreadId(null);
            }
          }
          return;
        }
        if (cancelled) return;
        setThreadId(id);
        setMessages(json.data.messages);
        setUnread(json.data.thread.guest_unread);
        setHandler(json.data.thread.handler_name);
        setStaffReadUpto(json.data.thread.staff_read_upto);
      } catch {
        // Non-fatal: the form still works.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [creds, headers, storageKey]);

  // Live updates over SSE (credentials in the query string: EventSource cannot set headers). Auto-reconnects.
  useEffect(() => {
    if (!creds || !threadId) return;
    const qs = new URLSearchParams({ thread_id: threadId });
    if (creds.kind === "inquiry") qs.set("token", creds.token);
    else {
      qs.set("booking_reference", creds.reference);
      qs.set("access_token", creds.accessToken);
    }
    const es = new EventSource(`/api/public/chat/stream?${qs.toString()}`);
    es.onopen = () => setOffline(false);
    es.onerror = () => setOffline(true);
    es.addEventListener("message", (ev) => {
      const { message } = JSON.parse((ev as MessageEvent).data) as { message: Message };
      setMessages((prev) => (prev.some((m) => m.id === message.id) ? prev : [...prev, message]));
      if (message.sender_type !== "GUEST") {
        if (activeRef.current) markRead(creds, threadId);
        else setUnread((n) => n + 1);
      }
    });
    es.addEventListener("read", (ev) => {
      const data = JSON.parse((ev as MessageEvent).data) as { by: string; upto: number };
      if (data.by === "STAFF") setStaffReadUpto((prev) => Math.max(prev, data.upto));
    });
    // Reassignment: keep "who is responding" current.
    es.addEventListener("thread", (ev) => {
      setHandler((JSON.parse((ev as MessageEvent).data) as { handler_name: string | null }).handler_name);
    });
    return () => es.close();
  }, [creds, threadId, markRead]);

  useEffect(() => {
    if (active) {
      bottomRef.current?.scrollIntoView({ block: "end" });
      if (creds && threadId && unread > 0) markRead(creds, threadId);
    }
  }, [active, messages.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const start_ = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const fields: Record<string, string> =
        creds?.kind === "booking"
          ? { booking_reference: creds.reference, access_token: creds.accessToken, message: draft }
          : { ...(start.kind === "room" ? { unit_token: start.unitToken } : { hostname: start.hostname }), name, contact, message: draft };
      const res = await fetch("/api/public/chat/threads", {
        method: "POST",
        ...(pending.images.length > 0
          ? { body: buildChatForm(fields, pending.images) }
          : { headers: { "Content-Type": "application/json" }, body: JSON.stringify(fields) }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error);
      setThreadId(json.data.thread.id);
      setMessages(json.data.messages);
      setHandler(json.data.thread.handler_name);
      setStaffReadUpto(json.data.thread.staff_read_upto);
      setDraft("");
      pending.clear();
      if (json.data.token) {
        const c: Creds = { kind: "inquiry", threadId: json.data.thread.id, token: json.data.token };
        try { localStorage.setItem(storageKey, JSON.stringify(c)); } catch { /* ignore */ }
        setCreds(c);
      }
    } catch (err) {
      setError(localizeError(locale, err instanceof Error ? err.message : String(err)));
    } finally {
      setBusy(false);
    }
  };

  const send = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy || (!draft.trim() && pending.images.length === 0) || !creds || !threadId) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`/api/public/chat/threads/${threadId}/messages`, {
        method: "POST",
        ...(pending.images.length > 0
          ? { headers: headers(creds), body: buildChatForm({ body: draft }, pending.images) }
          : { headers: { "Content-Type": "application/json", ...headers(creds) }, body: JSON.stringify({ body: draft }) }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error);
      setMessages((prev) => (prev.some((m) => m.id === json.data.id) ? prev : [...prev, json.data]));
      setDraft("");
      pending.clear();
    } catch (err) {
      setError(localizeError(locale, err instanceof Error ? err.message : String(err)));
    } finally {
      setBusy(false);
    }
  };

  const hasThread = messages.length > 0;
  const bookingMode = creds?.kind === "booking";
  const nameRequired = start.kind === "site";
  const canSend = draft.trim().length > 0 || pending.images.length > 0;
  // "Seen" goes under the guest's latest message that staff have read.
  const lastSeenOwnId = messages.reduce((id, m) => (m.sender_type === "GUEST" && m.id <= staffReadUpto ? Math.max(id, m.id) : id), 0);
  const guestHeaders = creds ? headers(creds) : undefined;
  const onKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      e.currentTarget.form?.requestSubmit();
    }
  };
  // Paste is handled on the textarea itself; drag-and-drop works anywhere on the form.
  const composerProps = { onDrop: pending.onDrop, onDragOver: pending.onDragOver };
  const shownError = error ?? pending.error;

  return (
    <>
      {hasThread && (
        <div className="ps-chat-handler" role="status">
          {handler ? t("chat.guest.handler", { name: handler }) : t("chat.guest.waiting")}
        </div>
      )}

      <div className="ps-chat-body">
        {!hasThread && <p className="ps-chat-intro">{start.kind === "room" ? t("chat.guest.roomIntro") : t("chat.guest.intro")}</p>}
        {messages.map((m) => (
          <div key={m.id} className={`ps-chat-msg ${m.sender_type === "GUEST" ? "mine" : ""}`}>
            {(m.body || m.attachments.length === 0) && <div className="ps-chat-bubble">{m.body}</div>}
            {m.attachments.map((a) => (
              <ChatImage key={a.id} id={a.id} guestHeaders={guestHeaders} />
            ))}
            <span>
              {m.sender_type === "GUEST" ? t("chat.guest.you") : m.sender_name || t("chat.guest.staff")} · {parseTime(m.created_at).toLocaleTimeString(locale, { timeStyle: "short" })}
              {m.id === lastSeenOwnId && <> · <span className="cm-seen">{t("chat.guest.seen")}</span></>}
            </span>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      {offline && <p className="ps-chat-note">{t("chat.guest.offline")}</p>}
      {shownError && <p className="ps-chat-note err" role="alert">{shownError}</p>}

      {hasThread ? (
        <>
          <PendingStrip images={pending.images} onRemove={pending.remove} />
          <form className="ps-chat-form" onSubmit={send} {...composerProps}>
            <AttachButton onFiles={pending.add} disabled={busy} />
            <textarea value={draft} onChange={(e) => setDraft(e.target.value)} placeholder={t("chat.guest.message")} rows={2} maxLength={2000} onKeyDown={onKey} onPaste={pending.onPaste} />
            <button type="submit" disabled={busy || !canSend}>{t("chat.guest.send")}</button>
          </form>
        </>
      ) : (
        <>
          <PendingStrip images={pending.images} onRemove={pending.remove} />
          <form className="ps-chat-form col" onSubmit={start_} {...composerProps}>
            {!bookingMode && (
              <>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={nameRequired ? t("chat.guest.name") : t("chat.guest.nameOptional")}
                  required={nameRequired}
                  maxLength={80}
                  autoComplete="name"
                />
                <input value={contact} onChange={(e) => setContact(e.target.value)} placeholder={t("chat.guest.contact")} maxLength={120} aria-describedby="ps-chat-contact-hint" />
                <small id="ps-chat-contact-hint">{t("chat.guest.contactHint")}</small>
              </>
            )}
            <div className="ps-chat-row">
              <AttachButton onFiles={pending.add} disabled={busy} />
              <textarea value={draft} onChange={(e) => setDraft(e.target.value)} placeholder={t("chat.guest.startMessage")} rows={3} required={pending.images.length === 0} maxLength={2000} onKeyDown={onKey} onPaste={pending.onPaste} />
            </div>
            <button type="submit" disabled={busy || !canSend || (!bookingMode && nameRequired && !name.trim())}>{t("chat.guest.start")}</button>
          </form>
        </>
      )}
    </>
  );
}
